import { useState, useCallback } from "react";
import { captureScreenFrame, type DisplaySurface } from "@/lib/screenCapture";
import { resizeImageForApi } from "@/lib/imageResize";

export interface UseScreenCaptureOptions {
  /** Resize to this max width before returning (saves tokens). 0 = no resize. */
  maxWidth?: number;
  /** Display surface preference. */
  displaySurface?: DisplaySurface;
  highDpi?: boolean;
}

export function useScreenCapture(options: UseScreenCaptureOptions = {}) {
  const [isCapturing, setIsCapturing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const capture = useCallback(
    async (opts?: { displaySurface?: DisplaySurface }) => {
      setIsCapturing(true);
      setError(null);
      try {
        const dataUrl = await captureScreenFrame({
          displaySurface: opts?.displaySurface ?? options.displaySurface ?? "monitor",
          highDpi: options.highDpi ?? true,
        });
        const maxWidth = options.maxWidth ?? 1024;
        const finalUrl =
          maxWidth > 0 ? await resizeImageForApi(dataUrl, { maxWidth }) : dataUrl;
        return finalUrl;
      } catch (e) {
        const message = e instanceof Error ? e.message : "Capture failed";
        setError(message);
        throw e;
      } finally {
        setIsCapturing(false);
      }
    },
    [options.displaySurface, options.highDpi, options.maxWidth]
  );

  const clearError = useCallback(() => setError(null), []);

  return { capture, isCapturing, error, clearError };
}
